import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  IconButton,
  Autocomplete,
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  color: string;
}

interface ActiveStudent extends Student {
  order: number;
}

const InProgress: React.FC = () => {
  const [activeStudents, setActiveStudents] = useState<ActiveStudent[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [suggestedStudents, setSuggestedStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<ActiveStudent | null>(null);
  const [rightTabValue, setRightTabValue] = useState(0);
  const [searchValue, setSearchValue] = useState<Student | null>(null);
  const [draggedStudent, setDraggedStudent] = useState<Student | null>(null);

  useEffect(() => {
    // Fetch all students
    const fetchStudents = async () => {
      try {
        const response = await api.get('/students/');
        setAllStudents(response.data);
        // Initially show 5 random students as suggestions
        const shuffled = [...response.data].sort(() => 0.5 - Math.random());
        setSuggestedStudents(shuffled.slice(0, 5));
      } catch (error) {
        console.error('Error fetching students:', error);
      }
    };
    fetchStudents();
  }, []);

  const handleAddStudent = (student: Student) => {
    if (!activeStudents.find(s => s.id === student.id)) {
      const newActiveStudent: ActiveStudent = {
        ...student,
        order: activeStudents.length,
      };
      setActiveStudents([...activeStudents, newActiveStudent]);
      // Remove from suggestions
      setSuggestedStudents(suggestedStudents.filter(s => s.id !== student.id));
      // Select the newly added student
      setSelectedStudent(newActiveStudent);
    }
  };

  const handleRemoveStudent = (studentId: number) => {
    const removed = activeStudents.find(s => s.id === studentId);
    setActiveStudents(activeStudents.filter(s => s.id !== studentId));
    if (removed && removed.id === selectedStudent?.id) {
      setSelectedStudent(null);
    }
    // Add back to suggestions if not already there
    if (removed && !suggestedStudents.find(s => s.id === removed.id)) {
      setSuggestedStudents([...suggestedStudents, removed]);
    }
  };

  const handleDragStart = (student: Student) => {
    setDraggedStudent(student);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedStudent) {
      handleAddStudent(draggedStudent);
      setDraggedStudent(null);
    }
  };

  const handleSearchSelect = (student: Student | null) => {
    if (student) {
      handleAddStudent(student);
      setSearchValue(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 100px)', gap: 2 }}>
      {/* Left Column - Students */}
      <Paper
        sx={{
          width: 250,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Active Students Section */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderBottom: '2px solid #e0e0e0',
          }}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Élèves en cours
            </Typography>
            <Autocomplete
              size="small"
              options={allStudents}
              getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
              value={searchValue}
              onChange={(_, newValue) => handleSearchSelect(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Rechercher un élève..."
                  variant="outlined"
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: option.color,
                      }}
                    />
                    {option.first_name} {option.last_name}
                  </Box>
                </li>
              )}
            />
          </Box>
          <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {activeStudents.length === 0 ? (
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{ p: 2, textAlign: 'center' }}
              >
                Glissez des élèves ici ou recherchez-les
              </Typography>
            ) : (
              activeStudents.map((student) => (
                <ListItem
                  key={student.id}
                  sx={{
                    mb: 1,
                    borderRadius: 1,
                    bgcolor: selectedStudent?.id === student.id ? 'action.selected' : 'transparent',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                  }}
                  onClick={() => setSelectedStudent(student)}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveStudent(student.id);
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      bgcolor: student.color,
                      mr: 1.5,
                    }}
                  />
                  <ListItemText
                    primary={`${student.first_name} ${student.last_name}`}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))
            )}
          </List>
        </Box>

        {/* Suggested Students Section */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Suggestions
            </Typography>
          </Box>
          <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {suggestedStudents.map((student) => (
              <ListItem
                key={student.id}
                draggable
                onDragStart={() => handleDragStart(student)}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  cursor: 'grab',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
                secondaryAction={<DragIcon fontSize="small" color="action" />}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: student.color,
                    mr: 1.5,
                  }}
                />
                <ListItemText
                  primary={`${student.first_name} ${student.last_name}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Paper>

      {/* Middle Column - Session Timeline */}
      <Paper
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {selectedStudent ? (
          <>
            <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: selectedStudent.color,
                  }}
                />
                <Typography variant="h6">
                  {selectedStudent.first_name} {selectedStudent.last_name}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Trame de la séance
              </Typography>
            </Box>
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Contenu de la trame de séance à implémenter...
              </Typography>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body1" color="textSecondary">
              Sélectionnez un élève pour voir la trame de sa séance
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Right Column - Navigation Tabs */}
      <Paper
        sx={{
          width: 300,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={rightTabValue}
          onChange={(_, newValue) => setRightTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: '1px solid #e0e0e0' }}
        >
          <Tab label="Cours" sx={{ minWidth: 60 }} />
          <Tab label="Exercices" sx={{ minWidth: 80 }} />
          <Tab label="Séances" sx={{ minWidth: 70 }} />
          <Tab label="Vidéos" sx={{ minWidth: 60 }} />
          <Tab label="Enseignants" sx={{ minWidth: 90 }} />
        </Tabs>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {rightTabValue === 0 && (
            <Typography variant="body2">Liste des cours à implémenter...</Typography>
          )}
          {rightTabValue === 1 && (
            <Typography variant="body2">Liste des exercices à implémenter...</Typography>
          )}
          {rightTabValue === 2 && (
            <Typography variant="body2">Liste des séances à implémenter...</Typography>
          )}
          {rightTabValue === 3 && (
            <Typography variant="body2">Liste des vidéos à implémenter...</Typography>
          )}
          {rightTabValue === 4 && (
            <Typography variant="body2">Liste des enseignants à implémenter...</Typography>
          )}
        </Box>
      </Paper>

      {/* Future: Fourth Column - Student Objectives/Quizzes/Video */}
      {/* This will be implemented later */}
    </Box>
  );
};

export default InProgress;
